// Native (Capacitor / Android) bootstrap.
// Safe to call on web — every block guards on Capacitor.isNativePlatform().

import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import './push'; // Side-effect import: registers window._registerFcmPush
import './back'; // Side-effect import: registers window._handleNativeBack
import './imagePicker'; // Side-effect import: registers window._pickImageFile
import './links'; // Side-effect import: external links open in Custom Tab + window.openExternal
import './debugOverlay'; // Side-effect: shows on-device console when REACT_APP_DEBUG_OVERLAY=1
import './panelGestures'; // Side-effect: swipe-to-close for native fullscreen panels

const isNative = () => Capacitor.isNativePlatform?.() === true;

export const initNative = () => {
  if (!isNative()) return;

  document.documentElement.classList.add('is-native', 'is-android');

  setupStatusBar();
  setupSplash();
  setupKeyboard();
  setupBackButton();
  setupAppLifecycle();
  setupDeepLinks();
  setupNetwork();
  setupInputScroll();
};

// With adjustResize the WebView shrinks; ensure the focused field is visible.
// IMPORTANT: scroll only the field's OWN scroll container — never the document,
// which would drag the in-flow nav bar out of view. Also reset any document
// scroll the WebView applied automatically on focus.
const findScrollableParent = (el) => {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 4) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const setupInputScroll = () => {
  document.addEventListener('focusin', (e) => {
    const el = e.target;
    if (!el || !el.matches?.('input, textarea, [contenteditable="true"]')) return;
    setTimeout(() => {
      const parent = findScrollableParent(el);
      if (parent) {
        const er = el.getBoundingClientRect();
        const pr = parent.getBoundingClientRect();
        if (er.bottom > pr.bottom - 12 || er.top < pr.top + 12) {
          const target = parent.scrollTop + (er.top - pr.top) - (pr.height / 2 - er.height / 2);
          parent.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
        }
        // Field lives inside a fixed panel → the document must NOT scroll
        // (that's what was dragging the nav bar out of view). Reset it.
        if (window.scrollY) window.scrollTo(0, 0);
        const se = document.scrollingElement;
        if (se && se.scrollTop) se.scrollTop = 0;
      }
    }, 280);
  });
};

const setupStatusBar = async () => {
  // Each call individually catch'd; some methods are deprecated/UNIMPLEMENTED
  // on certain Android versions or plugin builds.
  try { await StatusBar.setOverlaysWebView({ overlay: false }); } catch (_) {}
  try { await StatusBar.setStyle({ style: Style.Dark }); } catch (_) {}
  try { await StatusBar.setBackgroundColor({ color: '#000000' }); } catch (_) {}
};

const setupSplash = async () => {
  // Hide once React has mounted + first paint happens.
  // The capacitor.config launchShowDuration=0 means it auto-hides, but we
  // also force-hide here so a slow first render doesn't leave it on.
  try {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
      });
    });
  } catch (e) { /* ignore */ }
};

const setupKeyboard = () => {
  // Each plugin call is individually catch'd because some methods (e.g.
  // setAccessoryBarVisible) are iOS-only and reject with UNIMPLEMENTED on Android.
  // 'native' → the Activity uses windowSoftInputMode=adjustResize, so the
  // WebView itself shrinks to the area above the keyboard. Fixed fullscreen
  // panels (height based on 100vh) therefore fit above the keyboard and their
  // bottom inputs stay visible. The map footer + side buttons are hidden via
  // the .keyboard-open class so they don't float.
  Promise.resolve(Keyboard.setResizeMode({ mode: KeyboardResize.Native })).catch(() => {});
  Promise.resolve(Keyboard.setAccessoryBarVisible({ isVisible: false })).catch(() => {});

  try {
    Keyboard.addListener('keyboardDidShow', (info) => {
      document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
      document.documentElement.classList.add('keyboard-open');
    });
    Keyboard.addListener('keyboardDidHide', () => {
      document.documentElement.style.setProperty('--keyboard-height', '0px');
      document.documentElement.classList.remove('keyboard-open');
    });
  } catch (e) { console.warn('Keyboard listener:', e); }
};

const setupBackButton = () => {
  CapApp.addListener('backButton', ({ canGoBack }) => {
    // Priority order: close open modals/panels → web history → exit
    if (window._handleNativeBack && window._handleNativeBack()) return;
    if (canGoBack && window.history.length > 1) {
      window.history.back();
      return;
    }
    CapApp.exitApp();
  });
};

const setupAppLifecycle = () => {
  CapApp.addListener('appStateChange', ({ isActive }) => {
    window._appIsActive = isActive;
    window.dispatchEvent(new CustomEvent('nativeAppStateChange', { detail: { isActive } }));
  });
};

const setupDeepLinks = () => {
  CapApp.addListener('appUrlOpen', async ({ url }) => {
    if (!url) return;
    console.log('[deeplink] appUrlOpen:', url);

    // ── OAuth callback: linksy://auth-callback?code=… ──────────────────────
    if (url.includes('auth-callback')) {
      // Tell the auth-state listener this sign-in came from native OAuth so it
      // navigates into the app (no page reload happens on native).
      window._pendingOAuthLogin = true;
      try {
        const { supabase } = await import('../supabase/config');
        const { Browser } = await import('@capacitor/browser');
        // Custom-scheme URLs (linksy://) aren't always parsed by new URL();
        // extract code/error from both query and fragment defensively.
        const qIndex = url.indexOf('?');
        const query = qIndex >= 0 ? url.slice(qIndex + 1).split('#')[0] : '';
        const params = new URLSearchParams(query);
        const code = params.get('code');
        const errParam = params.get('error') || params.get('error_description');
        console.log('[deeplink] code present:', !!code, 'err:', errParam || 'none');

        if (errParam) {
          console.error('[deeplink] OAuth provider error:', errParam);
        } else if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) console.error('[deeplink] exchangeCodeForSession error:', error.message || error);
        } else {
          // Implicit fallback: tokens in the fragment.
          const hIndex = url.indexOf('#');
          const hash = hIndex >= 0 ? url.slice(hIndex + 1) : '';
          const hp = new URLSearchParams(hash);
          const access_token = hp.get('access_token');
          const refresh_token = hp.get('refresh_token');
          if (access_token && refresh_token) {
            await supabase.auth.setSession({ access_token, refresh_token });
          }
        }
        try { await Browser.close(); } catch (_) {}
      } catch (e) {
        console.error('[deeplink] OAuth handling failed:', e?.message || e);
      }
      return;
    }

    // ── Event deep link ──────────────────────────────────────────────────
    // Accepts ?event=<id> (shared web links) and /event/<id> or linksy://event/<id>.
    try {
      const qMatch = url.match(/[?&]event=([^&#]+)/);
      const pMatch = url.match(/event\/([^/?#]+)/);
      const match = qMatch || pMatch;
      if (match) {
        const eventId = decodeURIComponent(match[1]);
        // Auth-aware: opens on the user's map if logged in, or the guest map
        // (with event details) if not — never dumps a guest on the login page.
        const open = window._openSharedEvent || window._openEventFromNotification;
        if (open) {
          // Small delay so React has mounted on cold start.
          setTimeout(() => open(eventId), 400);
        }
      }
    } catch (e) { console.warn('appUrlOpen parse:', e); }
  });
};

const setupNetwork = async () => {
  try {
    const status = await Network.getStatus();
    window._networkOnline = status.connected;
    window.dispatchEvent(new CustomEvent('nativeNetworkChange', { detail: status }));
    Network.addListener('networkStatusChange', (s) => {
      window._networkOnline = s.connected;
      window.dispatchEvent(new CustomEvent('nativeNetworkChange', { detail: s }));
    });
  } catch (e) { console.warn('Network init:', e); }
};
